import React, { useId, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  ButtonBase,
  Collapse,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
  alpha,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Publish as PublishIcon,
  Style as StyleIcon,
  TableChart as TableChartIcon,
} from "@mui/icons-material";
import { formatAmount } from "../../utils/hooks/dataHandler";
import CopyTextButton from "./CopyTextButton";

const SourceDataTable = ({
  data = [],
  title = "Data Sources",
  sx = {},
  defaultExpanded = true,
  onCopy,
  onImport,
  columnConfig = {
    description: {
      label: "Description",
      width: "2fr",
      copyable: true,
      allowImport: true,
    },
    amount: {
      label: "Amount",
      width: "1fr",
      copyable: false,
      align: "right",
    },
    reference: {
      label: "Reference",
      width: "1fr",
      copyable: false,
      align: "center",
    },
  },
  compact = false,
  variant = "enhanced", // 'enhanced', 'minimal', 'detailed'
  canEdit = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const theme = useTheme();
  const contentId = useId();

  const formatValue = (value, type, row) => {
    if (type === "amount" && typeof value === "number") {
      return formatAmount(value);
    }

    if (type === "reference") {
      return row.source?.sch_no || row.reference || "";
    }

    return value || "";
  };

  const EmptyState = ({ message, description }) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: compact ? 3 : 4,
        px: 3,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1.5,
        }}
      >
        <TableChartIcon sx={{ fontSize: 22, color: "primary.main" }} />
      </Box>
      <Typography variant='body2' fontWeight={600} gutterBottom>
        {message}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {description}
      </Typography>
    </Box>
  );

  const getVariantStyles = () => {
    switch (variant) {
      case "minimal":
        return {
          card: {
            boxShadow: "none",
          },
          header: { bgcolor: "background.paper" },
        };
      case "detailed":
        return {
          card: {
            borderColor: alpha(theme.palette.primary.main, 0.2),
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.04)}`,
          },
          header: {
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.06
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
          },
        };
      default:
        return {
          card: {
            boxShadow: "none",
          },
          header: {
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.035
            )}, ${alpha(theme.palette.primary.main, 0.015)})`,
          },
        };
    }
  };

  const variantStyles = getVariantStyles();

  return (
    <Paper
      elevation={0}
      sx={[
        {
          minWidth: 0,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          overflow: "hidden",
          ...variantStyles.card,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <ButtonBase
        type='button'
        aria-expanded={isExpanded}
        aria-controls={contentId}
        sx={{
          width: "100%",
          px: { xs: 1.5, sm: 2 },
          py: compact ? 1.25 : 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1.5,
          textAlign: "left",
          color: "text.primary",
          ...variantStyles.header,
          transition: "background-color 0.2s ease",
          "&:hover": {
            background: alpha(theme.palette.primary.main, 0.06),
          },
          "&.Mui-focusVisible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: -2,
          },
          borderBottom: isExpanded ? "1px solid" : 0,
          borderColor: "divider",
        }}
        onClick={() => setIsExpanded((current) => !current)}
      >
        <Stack
          component='span'
          direction='row'
          spacing={1.25}
          alignItems='center'
          sx={{ minWidth: 0, flex: 1 }}
        >
          <Box
            component='span'
            sx={{
              borderRadius: 2,
              width: compact ? 32 : 36,
              height: compact ? 32 : 36,
              flexShrink: 0,
              bgcolor: alpha(theme.palette.primary.main, 0.07),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            }}
          >
            <StyleIcon
              sx={{
                fontSize: compact ? 18 : 20,
                color: theme.palette.primary.main,
              }}
            />
          </Box>

          <Typography
            component='span'
            variant='subtitle2'
            sx={{
              fontWeight: 600,
              fontSize: compact ? "0.875rem" : "0.9375rem",
              lineHeight: 1.5,
              overflowWrap: "anywhere",
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Stack
          component='span'
          direction='row'
          spacing={1}
          alignItems='center'
          sx={{ flexShrink: 0 }}
        >
          <Chip
            component='span'
            label={`${data?.length || 0} ${
              data?.length === 1 ? "item" : "items"
            }`}
            size='small'
            sx={{
              borderRadius: 1.5,
              fontSize: "0.75rem",
              fontWeight: 600,
              height: 24,
              bgcolor: alpha(theme.palette.primary.main, 0.07),
              color: "primary.main",
            }}
          />

          <Box
            component='span'
            sx={{
              display: "inline-flex",
              color: "text.secondary",
              transition: "transform 0.2s ease",
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: 20 }} />
          </Box>
        </Stack>
      </ButtonBase>

      <Collapse in={isExpanded}>
        <Box id={contentId}>
          {data && data.length > 0 ? (
            <Box
              sx={{
                maxHeight: compact ? 300 : 400,
                overflow: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: `${alpha(
                  theme.palette.primary.main,
                  0.25
                )} transparent`,
                "&::-webkit-scrollbar": {
                  width: 8,
                  height: 8,
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  borderRadius: 4,
                  background: alpha(theme.palette.primary.main, 0.25),
                  "&:hover": {
                    background: alpha(theme.palette.primary.main, 0.4),
                  },
                },
              }}
            >
              <Table
                stickyHeader
                size={compact ? "small" : "medium"}
                aria-label={title}
                sx={{ minWidth: 480 }}
              >
                <TableHead
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    bgcolor: "background.paper",
                  }}
                >
                  <TableRow>
                    {Object.entries(columnConfig).map(([key, config]) => (
                      <TableCell
                        key={key}
                        scope='col'
                        align={config.align || "left"}
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.035),
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          color: "text.secondary",
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          px: { xs: 1.5, sm: 2 },
                          py: compact ? 1 : 1.5,
                          whiteSpace: "nowrap",
                          position: "static",
                        }}
                      >
                        {config.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, index) => (
                    <TableRow
                      key={`source-data-${index}`}
                      sx={{
                        "&:nth-of-type(even)": {
                          bgcolor: alpha(theme.palette.text.primary, 0.015),
                        },
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.045),
                        },
                        "&:focus-within": {
                          bgcolor: alpha(theme.palette.primary.main, 0.045),
                        },
                        "&:last-child .MuiTableCell-root": { borderBottom: 0 },
                        transition: "background-color 0.15s ease",
                      }}
                    >
                      {Object.entries(columnConfig).map(([key, config]) => (
                        <TableCell
                          key={key}
                          align={config.align || "left"}
                          sx={{
                            borderColor: "divider",
                            py: compact ? 1 : 1.5,
                            px: { xs: 1.5, sm: 2 },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                              minWidth: 0,
                              justifyContent:
                                config.align === "right"
                                  ? "flex-end"
                                  : config.align === "center"
                                    ? "center"
                                    : "flex-start",
                            }}
                          >
                            <Typography
                              variant='body2'
                              sx={{
                                fontWeight: 600,
                                color:
                                  key === "reference"
                                    ? "text.secondary"
                                    : "text.primary",
                                fontSize: compact ? "0.8125rem" : "0.875rem",
                                lineHeight: 1.6,
                                minWidth: 0,
                                overflowWrap: "anywhere",
                                whiteSpace:
                                  key === "amount" ? "nowrap" : "normal",
                              }}
                            >
                              {formatValue(row[key], key, row)}
                            </Typography>
                            {canEdit && config.copyable && row[key] && (
                              <Box
                                sx={{ display: "inline-flex", flexShrink: 0 }}
                              >
                                <CopyTextButton
                                  text={String(row[key])}
                                  tooltipText={`Copy ${config.label.toLowerCase()}`}
                                  size='small'
                                  onCopy={onCopy}
                                  sx={{
                                    opacity: 0.75,
                                    borderRadius: 1,
                                    "&:hover": {
                                      opacity: 1,
                                      color: "primary.main",
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.08
                                      ),
                                      transform: "none",
                                    },
                                  }}
                                />
                              </Box>
                            )}
                            {canEdit &&
                              config.allowImport &&
                              row[key] &&
                              typeof onImport === "function" && (
                                <Box
                                  sx={{ display: "inline-flex", flexShrink: 0 }}
                                >
                                  <CopyTextButton
                                    text={String(row[key])}
                                    tooltipText='Import row to schedule'
                                    size='small'
                                    onCopy={() => onImport(row)}
                                    icon={PublishIcon}
                                    copiedTooltipText='Done!'
                                    sx={{
                                      opacity: 1,
                                      color: "primary.main",
                                      borderRadius: 1,
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.06
                                      ),
                                      "&:hover": {
                                        bgcolor: alpha(
                                          theme.palette.primary.main,
                                          0.12
                                        ),
                                        transform: "none",
                                      },
                                    }}
                                  />
                                </Box>
                              )}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <EmptyState
              message='No data available'
              description='No available references for this schedule'
            />
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default SourceDataTable;
